import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import { useFieldTranslation } from "../../hooks";
import useFeature from "../../hooks/useFeature";
import { ConfigContext } from "../../utils/ConfigContext";
import {
  clickEventOnFeature,
  renderCell,
  standardizeColor,
  urlAndEmailRegex,
} from "../../utils/helper";
import { getLayerSymbology } from "../../utils/symbologies";
import { EditIcon } from "../Icons";
import ImageSwiper from "../MobileApp/MobilePopup/PopupBody/ImageSwiper/ImageSwiper";
import { ReportTag } from "../SharedInformation/SharedInformationCard/SharedInformationCard-styled";
import { IconButton } from "../UI";
import {
  StyledDot,
  StyledFeatureCardBody,
  StyledFeatureCardBodyText,
  StyledFeatureCardBodyTextContainer,
  StyledFeatureCardContainer,
  StyledFeatureCardHeader,
  StyledFeatureContainer,
  StyledFeatureImageContainer,
  StyledFeatureTitle,
  StyledFooter,
  StyledTranslatedText,
  StyledTranslationToggle,
} from "./FeatureCard-styled";

const attachmentsCache = new Map();

const FeatureCard = ({
  feature,
  layer,
  onImageLoad,
  selectedId,
  onEdit,
  isUpdate = false,
  additionalFields = [],
}) => {
  const { t, i18n } = useTranslation("common");
  const { config } = useContext(ConfigContext);
  const { title, iconUrl, dateTimeAgo } = useFeature(feature, t, i18n);
  const [images, setImages] = useState([]);
  const [_, startTransition] = useTransition();

  const {
    translation,
    isTranslating,
    showTranslation,
    renderTextWithLinks,
    toggleTranslation,
  } = useFieldTranslation(
    feature,
    layer.layerConfig?.updateCommentsFieldName || "",
    t,
    i18n
  );

  const symbologyFields = useMemo(() => {
    const layer = feature.sourceLayer ?? feature.layer;
    const result = [];
    if (layer) {
      const symboloy = getLayerSymbology(layer, config);
      const { colorMap } = symboloy || {};

      if (colorMap) {
        const colorMapFields = colorMap.field
          ? [colorMap.field]
          : colorMap.fields;
        const layerFields = layer.fields.filter((field) =>
          colorMapFields.includes(field.name)
        );

        const colors = layerFields.map((field) => {
          const color =
            colorMap.default[feature.attributes[field.name]] ??
            symboloy.defaultColor;
          const standartColor = standardizeColor(color);
          if (color)
            return {
              value: renderCell(
                field,
                feature.attributes[field.name],
                t,
                config
              ),
              bg: standartColor.includes("#ffffff") ? "#000" : standartColor,
              color: "#fff",
              field: field.name,
              title: t(
                "layer.fieldAlias." + field.name + ".title",
                field.alias
              ),
            };
        });

        result.push(...colors.filter((item) => item));
      }
    }

    return result;
  }, [feature, config, t]);

  useEffect(() => {
    setImages([]);
    if (!layer.capabilities.operations.supportsQueryAttachments) return;

    const objectId = feature.attributes[layer.objectIdField];

    // Check cache first
    if (attachmentsCache.has(objectId)) {
      setImages(attachmentsCache.get(objectId));
      if (onImageLoad) requestAnimationFrame(onImageLoad);
      return;
    }

    layer
      .queryAttachments({
        objectIds: [objectId],
      })
      .then((response) => {
        const attachments = Object.values(response).flat(1);
        const images =
          attachments?.filter((att) => att.contentType.startsWith("image")) ||
          [];

        startTransition(() => {
          setImages(images);
        });

        // Save to cache
        attachmentsCache.set(objectId, images);

        requestAnimationFrame(() => {
          if (onImageLoad) {
            onImageLoad();
          }
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, [feature, layer]);

  const hasReportingUpdate = useMemo(() => {
    return layer.layerConfig?.hasReportingUpdate;
  }, [layer]);

  const handleOpenPopup = () => {
    clickEventOnFeature(feature, {
      zoom: false,
      showPopup: true,
    });
  };

  const layerTitle = useMemo(() => {
    if (layer.getLayerTitle) {
      return layer.getLayerTitle(t);
    }
    return "";
  }, [layer, t]);

  const updateComment = useMemo(() => {
    const fieldName = layer.layerConfig?.updateCommentsFieldName || "";
    if (!fieldName || !feature.attributes) return;
    const value = feature.attributes[fieldName] || "";
    if (value === "") return;
    return {
      value: feature.attributes[fieldName] || "",
      fieldName: fieldName,
    };
  }, [layer, feature]);

  const selected = useMemo(() => {
    if (!feature) return false;
    return selectedId === feature.getObjectId();
  }, [selectedId, feature]);

  const isArabic = useMemo(() => {
    return i18n.language === "ar";
  }, [i18n.language]);

  const seeTranslationText = useMemo(() => {
    if (!translation) return null;

    return !showTranslation
      ? t("screen.popup.actions.translation.seeTranslated")
      : t("screen.popup.actions.translation.seeOriginal");
  }, [showTranslation, t]);

  function renderWithLinks(text) {
    if (!text) return text;
    const regex = urlAndEmailRegex;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const urlOrEmail = match[0];
      if (urlOrEmail.includes("@") && !urlOrEmail.startsWith("http")) {
        parts.push(
          <a
            key={key++}
            href={`mailto:${urlOrEmail}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textWrap: "nowrap" }}
          >
            {urlOrEmail}
          </a>
        );
      } else {
        const url = urlOrEmail.startsWith("http")
          ? urlOrEmail
          : `http://${urlOrEmail}`;
        parts.push(
          <a
            key={key++}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textWrap: "nowrap" }}
          >
            {urlOrEmail}
          </a>
        );
      }
      lastIndex = regex.lastIndex;
    }
    // Add any remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  }

  const additionalFieldsValues = useMemo(() => {
    if (!additionalFields || additionalFields.length === 0) return [];
    return additionalFields
      .map((fieldName) => {
        if (feature.attributes && fieldName in feature.attributes) {
          return feature.attributes[fieldName];
        }
        return null;
      })
      .filter((item) => item !== null);
  }, [additionalFields, feature]);

  if (isUpdate) {
    return (
      <StyledFeatureCardContainer
        dir={isArabic ? "rtl" : ""}
        selected={selected}
        isClickable={false}
      >
        <StyledFeatureCardHeader style={{ marginBottom: 5 }}>
          <StyledFeatureContainer>
            <StyledFeatureTitle className="text-elipsis">
              {t("screen.widget.Editor.manager.reports.updatesCardTitle")}
            </StyledFeatureTitle>
            {symbologyFields.length > 0
              ? symbologyFields.map((field, index) => (
                  <ReportTag bg={field.bg} key={index}>
                    <span>{field.value}</span>
                  </ReportTag>
                ))
              : null}
          </StyledFeatureContainer>
          {onEdit && (
            <IconButton width="22px" height="22px" onClick={onEdit}>
              <EditIcon width="12px" height="12px" />
            </IconButton>
          )}
        </StyledFeatureCardHeader>
        <StyledFeatureCardBody>
          <StyledFeatureCardBodyTextContainer>
            <StyledFeatureCardBodyText selected={selected}>
              {showTranslation ? (
                <>
                  <StyledTranslatedText>
                    {renderTextWithLinks(translation)}
                  </StyledTranslatedText>
                </>
              ) : updateComment ? (
                <StyledTranslatedText>
                  {renderTextWithLinks(updateComment.value)}
                </StyledTranslatedText>
              ) : null}
              {seeTranslationText && (
                <>
                  <StyledDot />
                  <StyledTranslationToggle onClick={toggleTranslation}>
                    {seeTranslationText}
                  </StyledTranslationToggle>
                </>
              )}
            </StyledFeatureCardBodyText>
          </StyledFeatureCardBodyTextContainer>
        </StyledFeatureCardBody>
        <div style={{ marginTop: 4 }}>
          <ImageSwiper images={images} />
        </div>
        <div style={{ marginTop: 5, lineHeight: "normal", display: "flex" }}>
          <StyledTranslatedText style={{ color: "rgb(76,76,76)" }}>
            {additionalFieldsValues.map((value, idx) => (
              <Fragment key={idx}>
                {renderWithLinks(value)}
                {idx < additionalFieldsValues.length - 1 && <StyledDot />}
              </Fragment>
            ))}
          </StyledTranslatedText>
        </div>
        <StyledFooter>
          {dateTimeAgo && (
            <>
              <span style={{ color: "gray" }}>{dateTimeAgo}</span>
            </>
          )}
        </StyledFooter>
      </StyledFeatureCardContainer>
    );
  } else {
    return (
      <StyledFeatureCardContainer
        dir={isArabic ? "rtl" : ""}
        onClick={handleOpenPopup}
        selected={selected}
      >
        <StyledFeatureCardHeader>
          <StyledFeatureImageContainer>
            <img src={iconUrl} />
          </StyledFeatureImageContainer>
          <StyledFeatureContainer>
            <StyledFeatureTitle className="text-elipsis">
              {title}
            </StyledFeatureTitle>
          </StyledFeatureContainer>
          {onEdit && (
            <IconButton width="22px" height="22px" onClick={onEdit}>
              <EditIcon width="12px" height="12px" />
            </IconButton>
          )}
        </StyledFeatureCardHeader>
        <StyledFeatureCardBody>
          <StyledFeatureCardBodyTextContainer>
            <StyledFeatureCardBodyText selected={selected}>
              {symbologyFields.length > 0
                ? symbologyFields.map((field, index) => (
                    <Fragment key={index}>
                      <span
                        style={{
                          color: field.bg,
                          fontWeight: 500,
                          fontSize: 10,
                        }}
                      >
                        {field.value}
                      </span>
                      <StyledDot />
                    </Fragment>
                  ))
                : null}
              {showTranslation ? (
                <>
                  <StyledTranslatedText>
                    {renderTextWithLinks(translation)}
                  </StyledTranslatedText>
                </>
              ) : updateComment ? (
                <StyledTranslatedText>
                  {renderTextWithLinks(updateComment.value)}
                </StyledTranslatedText>
              ) : null}
            </StyledFeatureCardBodyText>
          </StyledFeatureCardBodyTextContainer>
        </StyledFeatureCardBody>
        <div style={{ marginTop: 4 }}>
          <ImageSwiper images={images} />
        </div>
        <StyledFooter>
          {dateTimeAgo && (
            <>
              <span style={{ color: "gray" }}>{dateTimeAgo}</span>
            </>
          )}
          {seeTranslationText && (
            <>
              <StyledDot />
              <StyledTranslationToggle onClick={toggleTranslation}>
                {seeTranslationText}
              </StyledTranslationToggle>
            </>
          )}
        </StyledFooter>
      </StyledFeatureCardContainer>
    );
  }
};

export default FeatureCard;
